// src/components/GuidedTour.jsx
// The guided tour (3.53.0, roadmap #6; the owner's choices of 2026-10-10):
// the page is dimmed and one real part of it is lit up at a time (the menu,
// the side strips, the theme, Sign out…), with a short note beside it:
// Back · Next · Skip. Everyone gets it once (new people on their first
// sign-in, everyone else on their next one); Finish or Skip ends it for good
// (preferences.tourDone, saved to their account so it doesn't come back on
// another device). Help → Take the tour plays it again.
//
// Steps come from buildTourSteps() below and follow what the person can
// open: a page they can't open is never shown. Each step points at an
// element marked data-tour="…" (MainNav, SideRail, RightRail, BottomBar,
// the header, the menu under your name); a step without one (the welcome,
// fax by email, the end) is a note in the middle of the screen.
// The page can't be clicked while it runs; Escape skips it, ← and → move.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Emo from './Emo';

export const TOUR_VERSION = 1;   // raise it to show a changed tour to everyone again
const PAD = 6;                  // space around the lit-up part
const GAP = 14;                 // between it and the note

export default function GuidedTour({ steps, onEnd }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);       // the lit-up part, or null (note in the middle)
  const [pos, setPos] = useState(null);         // where the note sits
  const noteRef = useRef(null);
  const nextRef = useRef(null);
  const step = steps[i];
  const last = i === steps.length - 1;

  // Each step: tidy up after the one before, get the page ready (open a strip
  // or the menu under your name), then follow its part as it moves
  useEffect(() => {
    step.before?.();
    let alive = true, frame = 0, missing = 0;
    const started = Date.now();
    const track = () => {
      if (!alive) return;
      const el = step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null;
      const r = el?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        if (Date.now() - started < 60) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        setRect(prev => (prev && Math.abs(prev.left - r.left) < 0.5 && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5
          ? prev : { left: r.left, top: r.top, width: r.width, height: r.height }));
      } else if (step.target && ++missing > 40) {
        setRect(null);   // not on the page after all: say it in the middle
      } else if (!step.target) setRect(null);
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    const t = setTimeout(() => nextRef.current?.focus({ preventScroll: true }), 80);
    return () => { alive = false; cancelAnimationFrame(frame); clearTimeout(t); step.after?.(); };
  }, [step]);

  // The note beside the lit-up part: below it if there's room, else above,
  // to the right, to the left; always on the screen
  useLayoutEffect(() => {
    const note = noteRef.current;
    if (!note) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const w = note.offsetWidth, h = note.offsetHeight;
    const clampX = x => Math.max(12, Math.min(vw - w - 12, x));
    const clampY = y => Math.max(12, Math.min(vh - h - 12, y));
    if (!rect) { setPos({ left: (vw - w) / 2, top: Math.max(12, (vh - h) / 2.4), side: 'middle' }); return; }
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    const fits = {
      bottom: rect.top + rect.height + PAD + GAP + h < vh - 8,
      top: rect.top - PAD - GAP - h > 8,
      right: rect.left + rect.width + PAD + GAP + w < vw - 8,
      left: rect.left - PAD - GAP - w > 8,
    };
    const order = [step.place, 'bottom', 'top', 'right', 'left'].filter(Boolean);
    const side = order.find(s => fits[s]) || 'middle';
    const at = {
      bottom: { left: clampX(cx - w / 2), top: rect.top + rect.height + PAD + GAP },
      top: { left: clampX(cx - w / 2), top: rect.top - PAD - GAP - h },
      right: { left: rect.left + rect.width + PAD + GAP, top: clampY(cy - h / 2) },
      left: { left: rect.left - PAD - GAP - w, top: clampY(cy - h / 2) },
      middle: { left: (vw - w) / 2, top: Math.max(12, (vh - h) / 2.4) },
    }[side];
    setPos(prev => (prev && prev.side === side && Math.abs(prev.left - at.left) < 0.5 && Math.abs(prev.top - at.top) < 0.5 ? prev : { ...at, side }));
  }, [rect, i, step.place]);

  const go = useCallback(d => setI(n => Math.max(0, Math.min(steps.length - 1, n + d))), [steps.length]);
  const finish = useCallback(how => onEnd(how), [onEnd]);

  // ← → move, Escape skips; nothing else on the page reacts meanwhile
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(last ? 'finished' : 'skipped'); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); if (last) finish('finished'); else go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); go(-1); }
      else if (e.key === 'Tab') {   // keep the keyboard inside the note
        const els = [...(noteRef.current?.querySelectorAll('button') || [])];
        if (!els.length) return;
        const at = els.indexOf(document.activeElement);
        e.preventDefault();
        els[(at + (e.shiftKey ? -1 : 1) + els.length) % els.length].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [go, finish, last]);

  // the ring stays on the screen (a part at the very edge, like More on phones)
  const hole = rect && (() => {
    const l = Math.max(3, rect.left - PAD), t = Math.max(3, rect.top - PAD);
    const r = Math.min(window.innerWidth - 3, rect.left + rect.width + PAD), b = Math.min(window.innerHeight - 3, rect.top + rect.height + PAD);
    return { left: l, top: t, width: Math.max(0, r - l), height: Math.max(0, b - t) };
  })();
  // straight into <body>, so nothing on the page (pop-up notices included) sits above it
  return createPortal(
    // a <section>, not a <div>: the page-wide "blur behind pop-ups" rule (App.jsx,
    // div:has(> [role=dialog])) would otherwise blur the part that's lit up
    <section data-tour-ui="" aria-label="Guided tour" style={S.root}>
      <style>{CSS}</style>
      {/* catches clicks, so the page underneath isn't used by accident */}
      <div style={S.blocker} onMouseDown={e => e.preventDefault()} aria-hidden="true" />
      {hole
        ? <div className="cp-tour-hole" style={{ ...S.hole, ...hole, borderRadius: step.round ? Math.min(hole.width, hole.height) / 2 : 14 }} aria-hidden="true" />
        : <div className="cp-tour-dim" style={S.dim} aria-hidden="true" />}
      <div ref={noteRef} role="dialog" aria-modal="true" aria-labelledby="cp-tour-title" aria-describedby="cp-tour-body"
        className={`cp-tour-note${pos ? ' is-in' : ''}`} key={i}
        style={{ ...S.note, left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}>
        <div style={S.head}>
          {step.icon && <span style={S.icon} aria-hidden="true"><Emo e={step.icon} /></span>}
          <h2 id="cp-tour-title" style={S.title}>{step.title}</h2>
        </div>
        <div id="cp-tour-body" style={S.body}>{step.body}</div>
        <div style={S.progress} aria-hidden="true">
          {steps.map((s, n) => <span key={s.id} style={{ ...S.dot, ...(n === i ? S.dotOn : n < i ? S.dotDone : {}) }} />)}
        </div>
        <div style={S.actions}>
          <span style={S.count} aria-live="polite">{i + 1} of {steps.length}</span>
          <span style={{ flex: 1 }} />
          {i > 0 && <button type="button" onClick={() => go(-1)} style={S.back}>Back</button>}
          <button ref={nextRef} type="button" onClick={() => (last ? finish('finished') : go(1))} style={S.next}>
            {last ? 'Finish' : i === 0 ? 'Start the tour' : 'Next'}
          </button>
        </div>
        {!last && <button type="button" onClick={() => finish('skipped')} style={S.skip}>Skip the tour</button>}
      </div>
    </section>,
    document.body,
  );
}

// ── The steps ───────────────────────────────────────────────
// ctx: { phone, firstName, can(tabId), perm(permission), individual, hasSwitcher, superAdmin, training,
//        openMenu(), closeMenu(), setRail('left'|'right'|null), setMore(bool) }
export function buildTourSteps(ctx) {
  const { phone, can } = ctx;
  const rail = side => (phone ? {} : { before: () => ctx.setRail(side), after: () => ctx.setRail(null) });
  // pages in the left strip (computers) or under More (phones)
  const morePage = (id, icon, title, body) => (can(id) ? [{
    id, target: `tab-${id}`, icon, title, body, place: phone ? 'top' : 'right',
    ...(phone ? { before: () => ctx.setMore(true), after: () => ctx.setMore(false) } : rail('left')),
  }] : []);
  const menu = { before: ctx.openMenu, after: ctx.closeMenu };
  const faxEmail = ctx.individual ? 'privatefax@crazypixels.org' : 'fax@crazypixels.org';
  // 3.53.1: say only what their role lets them do (Managers see people but
  // don't add them or change roles, by default)
  const perm = ctx.perm || (() => false);
  const peopleDo = [perm('users.add') && 'add someone',
    ctx.individual ? perm('users.changeRole') && 'change their role'   // teams are for business clients only
      : (perm('users.changeRole') || perm('teams.manage')) && 'change their role or team'].filter(Boolean);
  const usersBody = peopleDo.length
    ? `The people at your company: ${peopleDo.join(', ')}${perm('activity.view') ? ', and see what’s been done' : ''}.`
    : `The people at your company and their roles${perm('activity.view') ? ', and what’s been done' : ''}.`;
  // Settings: only the sections their role opens (Settings.jsx, settings.<id>)
  const sections = [['settings.retries', 'fax retries'], ['settings.incomingEmails', 'incoming fax emails'], ['settings.coverSheet', 'cover sheets'],
    ['settings.lineNames', 'fax line names'], ['settings.blockedNumbers', 'blocked numbers'], ['settings.notices', 'notification emails'],
    ['settings.defaultLine', 'the default line'], ['reminders.view', 'reminders']].filter(([p]) => perm(p)).map(([, w]) => w).slice(0, 3);
  const andList = l => (l.length > 1 ? `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}` : l[0] || '');
  const settingsBody = sections.length ? `Your company’s fax settings, such as ${andList(sections)}.` : 'Your company’s fax settings.';
  const steps = [
    { id: 'welcome', icon: '👋', title: `Welcome to the Fax Portal${ctx.firstName ? `, ${ctx.firstName}` : ''}!`,
      body: <>Here’s a quick tour of where everything is. {phone ? 'Tap Next to move on.' : 'Use Next, or the ← → keys.'} You can skip it, and take it again any time from <strong>Help → Take the tour</strong>.</> },
    { id: 'overview', target: 'tab-dashboard', icon: '🏠', title: 'Overview', place: phone ? 'top' : 'bottom',
      body: 'Your faxes at a glance: new faxes, faxes still going out, and how your recent faxes went.' },
    ...(can('compose') ? [{ id: 'compose', target: 'tab-compose', icon: '✍️', title: 'Compose', place: phone ? 'top' : 'bottom',
      body: 'Send a fax: enter the fax number, add your files and a cover sheet if you like, then send it now or pick a time.' }] : []),
    ...(can('outgoing') ? [{ id: 'outbox', target: 'tab-outgoing', icon: '📤', title: 'Outbox', place: phone ? 'top' : 'bottom',
      body: `Sent faxes and how each one went: sending, delivered or failed.${perm('fax.resend') ? ' If one fails, you can send it again with Resend.' : ''}` }] : []),
    ...(can('incoming') ? [{ id: 'inbox', target: 'tab-incoming', icon: '📥', title: 'Inbox', place: phone ? 'top' : 'bottom',
      body: 'Faxes that arrive on your fax numbers. Ones not opened yet show as New, and the number beside Inbox counts faxes that arrived since you last opened it.' }] : []),
    ...(perm('fax.emailToFax') ? [{ id: 'email', icon: '✉️', title: 'Fax by email',
      body: <>You can also fax straight from your email: send your document to the fax number itself, like <strong>15550100142@crazypixels.org</strong>. Your subject and message become the cover sheet. Or email <strong>{faxEmail}</strong> with the number in the subject. Send it from the email address you use here.</> }] : []),
    phone
      ? { id: 'more', target: 'tab-more', icon: '☰', title: 'More', place: 'top',
          body: `Everything else is under More${can('kb') ? ': the Library, and any other pages your role has' : ', depending on your role'}.` }
      : { id: 'left', target: 'left-rail', icon: '»', title: 'More pages, on the left', place: 'right', ...rail('left'),
          body: 'Pages you open now and then live in this strip, grouped by what they’re for. Point at it to see their names; it slides away when you move off it.' },
    ...morePage('users', '🎭', ctx.individual ? 'Staff' : 'Users', usersBody),   // individual clients call it Staff
    ...morePage('settings', '🛠️', 'Settings', settingsBody),
    // 3.53.1 (owner): Usage is the monthly report; the fax-by-fax lists only with "usage details"
    ...morePage('usage', '👨🏻‍💻', 'Usage', `How many faxes and pages your company sent and received each month.${perm('usage.details') ? ' You can also see a list of every sent and received fax.' : ''}`),
    ...morePage('kb', '📚', 'Library', `Short guides and videos for your role.${ctx.training ? ' My training lists the ones to start with.' : ''}`),
    phone
      ? { id: 'right', target: 'me', icon: '👤', title: 'Your menu', place: 'bottom', round: true,
          body: <>Tap your picture for your profile, Preferences, Security and Help: How do I…?, Support, Feedback and What’s new. <strong>Take the tour</strong> is there too.</> }
      : { id: 'right', target: 'right-rail', icon: '«', title: 'Your account and Help, on the right', place: 'left', ...rail('right'),
          body: <>Your profile, notes, Preferences and Security, and Help: How do I…?, Support, Feedback and What’s new. <strong>Take the tour</strong> starts this tour again.</> },
    { id: 'signout', target: 'signout', icon: '↩', title: 'Sign out', place: phone ? 'bottom' : 'left', ...menu,
      body: phone
        ? 'Sign out is at the top of your menu. On a shared device, always sign out when you’re done.'
        : 'Point at your name for your details and Sign out. On a shared computer, always sign out when you’re done.' },
    { id: 'theme', target: 'theme', icon: '🎨', title: 'Colours and dark mode', place: 'bottom', ...(phone ? menu : {}),
      body: 'Pick a colour theme, and Light, Dark or Auto (follows your device). Your choice is saved to your account, so it follows you everywhere.' },
    ...(ctx.hasSwitcher ? [{ id: 'switcher', target: 'client-switcher', icon: '⇆', title: ctx.superAdmin ? 'Switch client' : 'Switch company', place: 'bottom', round: true,
      body: ctx.superAdmin
        ? 'Switch between All clients and one client here; the whole portal then shows only that client.'
        : 'You belong to more than one company. Switch here, and the whole portal shows that company’s faxes and pages.' }] : []),
    { id: 'end', icon: '🎉', title: 'You’re all set',
      body: <>Take this tour again any time from <strong>Help → Take the tour</strong>. Questions? Use <strong>How do I…?</strong> in Help, or contact Fax Portal support.</> },
  ];
  return steps;
}

const CSS = `
.cp-tour-hole { transition: left .35s cubic-bezier(.2,.8,.2,1), top .35s cubic-bezier(.2,.8,.2,1), width .35s cubic-bezier(.2,.8,.2,1), height .35s cubic-bezier(.2,.8,.2,1); }
.cp-tour-note { opacity: 0; transform: scale(.96); }
.cp-tour-note.is-in { opacity: 1; transform: none; transition: opacity .25s ease, transform .25s cubic-bezier(.2,.8,.2,1); }
.cp-tour-dim { animation: cpTourFade .25s ease; }
@keyframes cpTourFade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .cp-tour-hole, .cp-tour-note.is-in { transition: none; }
  .cp-tour-note { transform: none; }
  .cp-tour-dim { animation: none; }
}`;

const S = {
  root: { position: 'fixed', inset: 0, zIndex: 3000 },
  blocker: { position: 'fixed', inset: 0, background: 'transparent' },
  dim: { position: 'fixed', inset: 0, background: 'rgb(var(--c-000000-rgb) / 0.55)' },
  hole: { position: 'fixed', boxShadow: '0 0 0 200vmax rgb(var(--c-000000-rgb) / 0.55), 0 0 0 3px var(--c-26a69a), 0 0 22px 4px rgb(var(--c-26a69a-rgb) / 0.55)', pointerEvents: 'none' },
  note: { position: 'fixed', width: 'min(340px, calc(100vw - 24px))', boxSizing: 'border-box', background: 'var(--c-ffffff)', color: 'var(--t-263238)',
    borderRadius: 16, padding: '16px 18px 12px', boxShadow: '0 18px 48px rgb(var(--c-000000-rgb) / 0.35)', border: '1px solid var(--b-c8e6c9)' },
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  icon: { fontSize: 22, lineHeight: 1 },
  title: { margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--t-00695c)', lineHeight: 1.25 },
  body: { fontSize: 14.5, lineHeight: 1.5, color: 'var(--t-37474f)' },
  progress: { display: 'flex', gap: 5, flexWrap: 'wrap', margin: '12px 0 10px' },
  dot: { width: 7, height: 7, borderRadius: '50%', background: 'var(--c-cfd8dc)' },
  dotDone: { background: 'var(--c-80cbc4)' },
  dotOn: { background: 'var(--c-00897b)', width: 18, borderRadius: 4 },
  actions: { display: 'flex', alignItems: 'center', gap: 8 },
  count: { fontSize: 12.5, color: 'var(--t-78909c)', fontWeight: 600 },
  back: { padding: '9px 14px', borderRadius: 10, border: '1.5px solid var(--b-c8e6c9)', background: 'transparent', color: 'var(--t-37474f)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', minHeight: 40 },
  next: { padding: '9px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, var(--c-00897b), var(--c-26a69a) 55%, var(--b-43a047))', color: 'var(--t-ffffff)', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', minHeight: 40 },
  skip: { display: 'block', margin: '8px auto 0', padding: '4px 8px', border: 'none', background: 'none', color: 'var(--t-78909c)', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' },
};
